import { useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  useToast,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Inline,
  InlineItem,
  Stack,
  Icon,
  Input,
  Button,
  Content,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import type { ServiceAccountOauthClientSecret } from '@/api/organisation';

const IconPadding = styled.div({ paddingTop: 2 });

const ConnectorSecretConfirmation = (
  props: ConnectorSecretConfirmationProps,
) => {
  const { onDismiss = noop, credential, animation = true } = props;
  const toast = useToast('globalTop');
  const theme = useTheme() as any;

  const copy = useCallback(
    (value: string, label: string) => {
      navigator.clipboard.writeText(value);
      toast(`${label} copied to clipboard.`, {
        variant: 'success',
        duration: 3000,
      });
    },
    [toast],
  );

  return (
    <Modal
      aria-label='Connector credential created'
      width={496}
      animation={animation}
      onClose={onDismiss}
    >
      <ModalHeader>
        {credential.name
          ? `${credential.name} — connector created`
          : 'Connector created'}
      </ModalHeader>
      <ModalBody>
        <Stack space='medium' dividers>
          <Content padding='none' paddingBottom='medium'>
            <Inline alignY='top' space='small'>
              <InlineItem>
                <IconPadding>
                  <Icon
                    color={theme.color.yellow6}
                    magnitude='small'
                    type='exclamation-circle'
                  />
                </IconPadding>
              </InlineItem>
              <InlineItem sizing='fill-container'>
                Save the client secret now — you won&rsquo;t be able to see it
                again. Paste the client ID and secret into the connector&rsquo;s
                configuration.
              </InlineItem>
            </Inline>
          </Content>
          <Inline space='small' alignY='bottom'>
            <InlineItem sizing='fill-container'>
              <Input
                disabled
                label='Client ID'
                value={credential.clientId}
                magnitude='large'
              />
            </InlineItem>
            <InlineItem>
              <Button
                icon='copy'
                variant='secondary'
                onClick={() => copy(credential.clientId, 'Client ID')}
              >
                Copy
              </Button>
            </InlineItem>
          </Inline>
          <Inline space='small' alignY='bottom'>
            <InlineItem sizing='fill-container'>
              <Input
                disabled
                label='Client secret'
                value={credential.clientSecret}
                magnitude='large'
              />
            </InlineItem>
            <InlineItem>
              <Button
                icon='copy'
                variant='secondary'
                onClick={() => copy(credential.clientSecret, 'Client secret')}
              >
                Copy
              </Button>
            </InlineItem>
          </Inline>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Inline>
          <InlineItem sizing='fill-container' />
          <InlineItem>
            <Button onClick={onDismiss}>Done</Button>
          </InlineItem>
        </Inline>
      </ModalFooter>
    </Modal>
  );
};

type ConnectorSecretConfirmationProps = {
  onDismiss?: () => void;
  credential: ServiceAccountOauthClientSecret;
  animation?: boolean;
};

export type { ConnectorSecretConfirmationProps };
export { ConnectorSecretConfirmation };
